// useCreditPoolStream — live GTM credit-pool feed for the T5 operator header.
//
// Connects to GET {T6_BASE}/api/gtm/stream/credit-pool as a Server-Sent-Events
// stream, but via a FETCH reader rather than EventSource: EventSource cannot
// send the Firebase Bearer token the /api/gtm admin gate requires. Auth mirrors
// useGtm exactly — the same authHeader() (Firebase ID token in an
// Authorization: Bearer header) and the same T6_BASE.
//
// Returns { available, ceiling, consumed, reserved, status } where status is
// connecting | live | reconnecting | error. It paints from the snapshot the
// server sends on connect, then updates on each credit_pool event. On a dropped
// stream or error it reconnects with a short backoff, refreshing the token each
// attempt (Firebase tokens expire). It never throws into the header: a failed or
// non-200 fetch just sets status 'error' and keeps the last known numbers.

import { useEffect, useRef, useState } from 'react';
import { authHeader, T6_BASE } from './useGtm';

const STREAM_PATH = '/api/gtm/stream/credit-pool';
const RETRY_MS = 3000; // server heartbeats every 25s; reconnect a beat after a drop

// Pull the numeric pool fields out of a parsed SSE data payload, ignoring any
// other keys the server may add. Missing fields stay absent so we never clobber
// good numbers with nulls (snapshot and credit_pool carry the same shape).
function poolFields(d) {
  const out = {};
  ['available', 'ceiling', 'consumed', 'reserved'].forEach((k) => {
    if (d[k] != null) out[k] = d[k];
  });
  return out;
}

// Parse one SSE frame into its JSON data payload, or null.
//   data: {...}  → payload (may span multiple data: lines, joined by \n)
//   : ...        → heartbeat / comment, ignored
//   event:/id:   → carry no numbers we need; snapshot and credit_pool share a
//                  shape, so we treat every data frame identically
function parseFrame(frame) {
  const dataLines = [];
  frame.split('\n').forEach((raw) => {
    const line = raw.replace(/\r$/, '');
    if (!line || line.startsWith(':')) return; // heartbeat / blank
    if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  });
  if (dataLines.length === 0) return null;
  try {
    return JSON.parse(dataLines.join('\n'));
  } catch {
    return null;
  }
}

export default function useCreditPoolStream() {
  const [pool, setPool] = useState({
    available: null,
    ceiling: null,
    consumed: null,
    reserved: null,
  });
  const [status, setStatus] = useState('connecting');

  const abortRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const scheduleReconnect = () => {
      if (cancelled) return;
      // Keep 'error' sticky (e.g. a 401 that won't fix itself on retry); a clean
      // drop after being live shows as 'reconnecting' instead.
      setStatus((s) => (s === 'error' ? s : 'reconnecting'));
      timerRef.current = setTimeout(connect, RETRY_MS);
    };

    const connect = async () => {
      if (cancelled) return;
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const headers = await authHeader(); // fresh token each attempt
        const res = await fetch(`${T6_BASE}${STREAM_PATH}`, {
          headers: { ...headers, Accept: 'text/event-stream' },
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          // 401/403/5xx or no stream body — surface as error, keep last numbers.
          if (!cancelled) setStatus('error');
          scheduleReconnect();
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line.
          let sep;
          while ((sep = buffer.indexOf('\n\n')) !== -1) {
            const payload = parseFrame(buffer.slice(0, sep));
            buffer = buffer.slice(sep + 2);
            if (payload && !cancelled) {
              setPool((prev) => ({ ...prev, ...poolFields(payload) }));
              setStatus('live');
            }
          }
        }
        // Reader ended (server closed / heartbeat gap) — reconnect.
        scheduleReconnect();
      } catch (err) {
        // AbortError is our own teardown; anything else is a real drop.
        if (cancelled || (err && err.name === 'AbortError')) return;
        setStatus('error');
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return { ...pool, status };
}
